<template>
    <seismic-layout>
        <main class="identify-page">
            <section class="header">
                <div class="container">
                    <h2>2. Identify your brace and Find Seismic Coefficient (C<sub>p</sub>)</h2>
                    <p>
                        Name your brace, enter a drawing reference and choose a method below for finding the Seismic Coefficient for your project.
                    </p>
                </div>
            </section>
            <section>
                <div class="container">
                    <h3>Identify your Brace</h3>
                    <fieldset class="flex notes">
                        <div class="triple">
                            <div class="text">
                                <label class="required" for="brace-name">Brace Name</label>
                                <input type="text" v-model="record.name" id="brace-name" placeholder="Name your brace" required>
                                <seismic-errors :errors="errors" prop="name"></seismic-errors>
                            </div>
                            <div class="text">
                                <label for="drawing-ref">Drawing Reference</label>
                                <input type="text" v-model="record.drawingReference" id="drawing-ref" placeholder="Enter reference">
                                <seismic-errors :errors="errors" prop="drawingReference"></seismic-errors>
                            </div>
                            <div class="text">
                                <label class="compact" for="quantity">Quantity</label>
                                <input type="number" v-model="record.quantity" id="quantity" min="1">
                                <seismic-errors :errors="errors" prop="quantity"></seismic-errors>
                                <div class="note">for Bill of Materials</div>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </section>
            <section class="cp-calc">
                <div class="container">
                    <h3>Find Seismic Coefficient (C<sub>p</sub>)</h3>
                    <p>
                        Pick a method that best suits your project needs.
                    </p>

                    <seismic-errors :errors="errors" prop="cp"></seismic-errors>

                    <fieldset>
                        <div class="radio">
                            <input type="radio" id="project-value" value="ss" v-model="record.calculationMethod" name="calc">
                            <label for="project-value">
                                Enter Your Project’s S<sub>s</sub> Value
                                <span class="note">(Per NFPA 13-2016 Table 9.3.5.9.3)</span>
                            </label>

                            <input type="radio" id="project-cp" value="cp" v-model="record.calculationMethod" name="calc">
                            <label for="project-cp">
                                Enter Your Project’s C<sub>p</sub>
                            </label>

                            <input type="radio" id="calculate-cp" value="asce" v-model="record.calculationMethod" name="calc">
                            <label for="calculate-cp">
                                Calculate Your Project’s C<sub>p</sub> Per ASCE/SEI 7-10
                                <span class="note">(Per NFPA 13-2016 Section 9.3.5.9.4)</span>
                            </label>
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'ss'" class="flex notes">
                        <div>
                            <div class="text flex">
                                <label class="required" for="ss">
                                    S<sub>s</sub>
                                </label>
                                <input-number v-model="ss" id="ss" placeholder="Enter your Ss value" required :min="0" :max="4" :step=".1" class="ss-input"/>
                                <p class="error simple" v-if="ssError" v-html="ssError"></p>

                                <p class="note">
                                    Refer to the USGS site to find site S<sub>s</sub> values:
                                    <a href="https://earthquake.usgs.gov/designmaps/beta/us/">
                                        U.S. Seismic Design Maps
                                    </a>
                                    <span>or</span>
                                    <a href="https://earthquake.usgs.gov/designmaps/us/application.php">
                                        try the older version
                                    </a>
                                </p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'asce'" class="flex notes full-input">
                        <div class="help">
                            <modal tooltip="click for help with ASCE" result="/docs/ASCE SEI 7-10 Seismic Horizontal Design Load Calculations per NFPA 13-2016.pdf" text="" link-classes="fa fa-question"/>
                        </div>
                        <div>
                            <div class="text">
                                <label class="required" for="ss">
                                    S<sub>s</sub>
                                </label>
                                <input-number v-model="ss" id="ss" placeholder="Enter your Ss value" required :min="0" :max="4.5" :step=".1" />
                                <p class="error simple" v-if="ssError" v-html="ssError"></p>
                            </div>

                            <div class="text">
                                <label class="required" for="site">
                                    Site
                                </label>
                                <div class="select">
                                    <select v-model="record.site" id="site" required placeholder="Select your Site value">
                                        <option v-for="site in validSites" :value="site.value" v-text="site.name"></option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div class="text">
                                <label class="required" for="z">
                                    z ({{ designIsMetric ? 'm.' : 'ft.' }})
                                </label>
                                <input-number :mToFt="designIsMetric" v-model="z" id="z" placeholder="Enter your z value" :min="0" step=".1" required />
                                <p class="note">Height of point of attachment from base</p>
                            </div>

                            <div class="text">
                                <label class="required" for="h">
                                    h ({{ designIsMetric ? 'm.' : 'ft.' }})
                                </label>
                                <input-number :mToFt="designIsMetric" v-model="h" id="h" placeholder="Enter your h value" :min="0" step=".1" required />
                                <p class="note">Avg roof structure height with respect to base</p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'cp'" class="flex notes">
                        <div>
                            <div class="text flex">
                                <label class="required" for="cp">
                                    C<sub>p</sub>
                                </label>
                                <input-number v-model="record.cp" id="cp" placeholder="Enter your Cp value" :min="0" step=".1" required />
                            </div>
                        </div>
                    </fieldset>

                    <div class="cp-value" v-if="calc">
                        C<sub>p</sub> =
                        <span v-if="calculating">
                            <i class="fa fa-refresh fa-spin fa-fw"></i>
                            Calculating
                        </span>
                        <span v-else-if="!canCalculate() || cp === null">
                            <i>Enter parameters</i>
                        </span>
                        <span v-else-if="cp === false">
                            <i>Invalid parameters. Please revise.</i>
                        </span>
                        <span v-else v-text="cp"></span>
                    </div>
                </div>
            </section>

            <section>
                <seismic-actions
                    v-on:save="saveThenReload"
                    v-on:next="saveThenNext"
                    :save-button-finished-text="saveButtonFinishedText"
                    :saving="saving"></seismic-actions>
            </section>
        </main>
        <aside class="primary">
            <seismic-sidebar :design="this.seismicDesignRecord" :brace="this.record"></seismic-sidebar>
        </aside>
    </seismic-layout>
</template>

<script>
    import InputNumber from 'vue/common/_inputNumber';
    import ClockvineMixin from 'vue/common/clockvine-mixin';
    import SeismicDesignLoader from './mixins/seismic-design-loader';
    import Modal from './_modal';

    import SeismicLayout from './layout';
    import SeismicHeader from './header';

    import SeismicErrors from './shared/errors';
    import SeismicSidebar from './shared/sidebar';
    import SeismicActions from './shared/actions';

    import axios from 'axios';
    import { debounce } from 'lodash';

    export default {
        components: {
            SeismicLayout,
            SeismicHeader,
            SeismicSidebar,
            SeismicSidebar,
            SeismicActions,
            SeismicErrors,
            InputNumber,
            Modal,
        },

        mixins: [
            ClockvineMixin,
            SeismicDesignLoader
        ],

        data() {
           return {
               type: 'seismicBraceConfiguration',

               record: {
                    seismicDesignId: this.seismicDesignId,
                    cp: null
               },
               saveButtonFinishedText: 'Saved!',

               ss: null,
               h: null,
               z: null,

               calculating: false,

               validSites: [
                   { name: 'A - Hard Rock', value: 'a' },
                   { name: 'B - Rock', value: 'b' },
                   { name: 'C - Very Dense Soil and Soft Rock', value: 'c' },
                   { name: 'D - Stiff Soil', value: 'd' },
                   { name: 'E - Soft Clay Soil', value: 'e' },
               ]
           }
        },

        mounted() {
            this.$store.dispatch('seismicSystem/fetchAll', { id: this.id });

            var $popup = $('.inline-popup');
            if ($popup.length) {
                $popup.magnificPopup({
                    type:'inline'
                });
            }
        },

        watch: {
            ss(value) {
                this.record.ss = parseFloat(value).toFixed(3).toString();
                this.calculateCp();
            },
            z(value) {
                this.record.z = parseFloat(value).toFixed(3).toString();
                this.calculateCp();
            },
            h(value) {
                this.record.h = parseFloat(value).toFixed(3).toString();
                this.calculateCp();
            },
            "record.site"() {
                this.calculateCp();
            },
            calc() {
                this.calculateCp();
            },
            "record.calculationMethod"() {
                this.$nextTick(() => {
                    var $popup = $('.inline-popup');
                    if ($popup.length) {
                        $popup.magnificPopup({
                            type:'inline'
                        });
                    }
                });
            },
            "record"() {
                this.ss = isNaN(parseFloat(this.record.ss)) ? null : parseFloat(this.record.ss);
                this.h = isNaN(parseFloat(this.record.h)) ? null : parseFloat(this.record.h);
                this.z = isNaN(parseFloat(this.record.z)) ? null : parseFloat(this.record.z);
            },
        },

        computed: {
            calc() {
                return this.record.calculationMethod;
            },
            cp() {
                return this.record.cp;
            },
            ssError() {
                return this.getSsError();
            },

            designIsMetric() {
                return this.seismicDesignRecord.useMetric == 1;
            },
        },

        methods: {
            getSsError() {
                if (this.ss === null) {
                    return null;
                }

                let ss = parseFloat(this.ss);

                if (this.calc === 'ss' && (ss < 0.0001 || ss > 4.000)) {
                    return "Please enter an S<sub>s</sub> value between 0.001 and 4.000";
                } else if (this.calc === 'asce' && (ss < 0.0001 || ss > 4.500)) {
                    return "Please enter an S<sub>s</sub> value between 0.001 and 4.500";
                }

                return null;
            },

            canCalculateWithSs() {
                return (
                    this.calc == 'ss' &&
                    this.record.ss &&
                    !this.getSsError()
                );
            },

            canCalculateWithAsce() {
                return (
                    this.calc == 'asce' &&
                    this.record.ss &&
                    this.record.site &&
                    this.record.z &&
                    this.record.h &&
                    !this.getSsError()
                );
            },

            canCalculate() {
                return (
                    this.calc == 'cp' ||
                    this.canCalculateWithSs() ||
                    this.canCalculateWithAsce()
                );
            },

            calculateCp() {
                if (this.canCalculate()) {
                    this.calculating = true;
                    this.calculateCpRequest();
                }
            },

            calculateCpRequest: (debounce(function() {
                // 'this' isn't properly bound with shorthand function
                axios.get('/actions/anvil-module/seismic-brace-configuration/calculate-cp', { params: this.record })
                    .then(r => { this.record.cp = r.data.cp; })
                    .catch(() => { this.calculatedCp = false; })
                    .then(() => { this.calculating = false; });
            }, 1000)),

            getUrlParams() {
                return { seismicDesignId: this.$route.params.seismicDesignId };
            },

            saveThenReload() {
                this.save().then(() => {
                    this.$router.push({name: 'seismic-brace-identify', params: {
                        id: this.record.id,
                        seismicDesignId: this.seismicDesignId
                    }});
                });
            },

            saveThenNext() {
                this.save().then(() => {
                    this.$router.push({name: 'seismic-brace-piping', params: {
                        id: this.record.id,
                        seismicDesignId: this.seismicDesignId
                    }});
                });
            },
        }
    }
</script>
